import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import Logo from '../components/Logo';

const StripeConnectOnboarding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [skipAttempts, setSkipAttempts] = useState(0);
  const [canSkip, setCanSkip] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Check if user already has Stripe Connect set up
    checkStripeConnectStatus();
  }, [user, navigate]);

  const checkStripeConnectStatus = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.stripeConnectAccountId && userData.stripeConnectStatus === 'active') {
          // User already has Stripe Connect set up
          navigate('/dashboard');
          return;
        }
      }
    } catch (error) {
      console.error('Error checking Stripe Connect status:', error);
    }
  };

  const handleStripeConnectSetup = async () => {
    setLoading(true);
    setError('');

    try {
      // Call the Stripe Connect setup function
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const createStripeConnectAccount = httpsCallable(functions, 'createStripeConnectAccount');
      
      const result = await createStripeConnectAccount({
        returnUrl: window.location.origin + '/home'
      });

      if (result.data.success && result.data.url) {
        // Redirect to Stripe Connect onboarding
        window.location.href = result.data.url;
      } else if (result.data.alreadySetUp) {
        // User already has account set up
        navigate('/home');
      } else {
        throw new Error('No redirect URL received');
      }

    } catch (error) {
      console.error('Error setting up Stripe Connect:', error);
      setError('Failed to set up payment account. Please try again.');
      setSkipAttempts(prev => prev + 1);
      
      // Allow skip after 2 failed attempts
      if (skipAttempts >= 1) {
        setCanSkip(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      // Mark user as having skipped Stripe Connect setup
      await setDoc(doc(db, 'users', user.uid), {
        stripeConnectSkipped: true,
        stripeConnectSkippedAt: new Date()
      }, { merge: true });

      // Redirect to dashboard with warning
      navigate('/dashboard?stripe_skipped=true');
    } catch (error) {
      console.error('Error skipping Stripe Connect:', error);
      setError('Failed to skip setup. Please try again.');
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Logo />
              <h1 className="ml-4 text-xl font-semibold text-white">Payment Setup</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-gray-800 rounded-lg p-8">
          <div className="text-center mb-8">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Connect Your Bank Account</h2>
            <p className="text-gray-300">
              Connect your bank account to get paid automatically when people buy your games.
              This is just like connecting your bank to Venmo or PayPal.
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-900 border border-red-700 rounded-lg">
              <p className="text-red-200">{error}</p>
            </div>
          )}

          {/* Benefits */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">What you'll be able to do:</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-green-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-300">Launch your own games for $15 each</span>
              </div>
              <div className="flex items-center">
                <svg className="h-5 w-5 text-green-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-300">Receive payments for marketplace game sales</span>
              </div>
              <div className="flex items-center">
                <svg className="h-5 w-5 text-green-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-300">Get paid automatically when your games sell</span>
              </div>
            </div>
          </div>

          {/* Setup Process */}
          <div className="mb-8 p-4 bg-gray-700 rounded-lg">
            <h3 className="text-lg font-semibold text-white mb-3">Setup Process:</h3>
            <div className="space-y-2 text-sm text-gray-300">
              <p>1. Click "Set Up Payment Account" below</p>
              <p>2. You'll be redirected to Stripe (our secure payment partner)</p>
              <p>3. Fill out your business information (takes 2-3 minutes)</p>
              <p>4. You'll be redirected back to your dashboard</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleStripeConnectSetup}
              disabled={loading}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium px-6 py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Connecting...' : 'Connect My Bank Account'}
            </button>
            
            {canSkip && (
              <button
                onClick={handleSkip}
                className="px-6 py-3 text-gray-300 hover:text-white border border-gray-600 rounded-lg"
              >
                Skip for Now
              </button>
            )}
          </div>

          {/* Warning for skipping */}
          {canSkip && (
            <div className="mt-4 p-3 bg-yellow-900 border border-yellow-700 rounded-lg">
              <p className="text-yellow-200 text-sm">
                <strong>Note:</strong> If you skip now, you'll be prompted to set up payments later. 
                You won't be able to launch games or receive payments until this is completed.
              </p>
            </div>
          )}

          {/* Back to login */}
          <div className="mt-6 text-center">
            <button
              onClick={handleBackToLogin}
              className="text-gray-400 hover:text-white text-sm"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripeConnectOnboarding;
